// src/i18n.js
import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from "react";

const STORAGE_KEY = "shotlist_lang";

export const SUPPORTED_LANGS = ["en", "fr"];

// Keep keys flat ("a.b.c") so it's easy to add / export later.
export const DICTS = {
  en: {
    // Changelog
    "changelog.title": "Changelog",
    "changelog.added": "Added",
    "changelog.improved": "Improved",
    "changelog.fixed": "Fixed",

    // Burger menu
    "menu.save": "Save project",
    "menu.import": "Import",
    "menu.import.script": "Import .fountain / .txt",
    "menu.import.project": "Import project (.json)",
    "menu.import.csv": "Import shot breakdown (.csv)",
    "menu.import.csv.needScript": "Import a script first",
    "menu.export": "Export",
    "menu.export.excel": "Excel / Sheets (.xlsx)",
    "menu.export.notion": "Notion (HTML + images)",
    "menu.export.full": "Full Package (.zip)",
    "menu.export.pdf": "PDF Canva Storyboard (A4)",
    "menu.export.script": "Script",
    "menu.export.scriptTxt": "Script as .txt",
    "menu.export.scriptFountain": "Script as .fountain",
    "menu.lexic": "Lexic (incoming)",
    "menu.wiki": "Wiki (incoming)",
    "menu.settings": "Settings",
    "menu.about": "About",

    // DICTS
    "script.refresh.deletedShots": "🗑️ {count} shot(s) deleted because their assigned text no longer exists.",

    // Save project popup
    saveProjectPopup_title: "You have saved your project !",
    saveProjectPopup_p1:
      "You have just downloaded your backup file in .json format to your computer.",
    saveProjectPopup_p2:
      "The application does not host your save data online. You must therefore keep it locally on your computer.",
    saveProjectPopup_p3:
      "To retrieve your project, you must import your backup file in the menu tab > Import > Import project (.json)",
    saveProjectPopup_ok: "I understand!",
    menu_saveProject: "Save Project",
    // Panels
    "panel.script.title": "SCRIPT",
    "panel.scenes.title": "SCENES & SHOTS",
    "badge.scenes": "Scenes: {count}",
    "badge.shots": "Shots: {count}",

    // View toggle
    "view.script": "Script View",
    "view.scene": "Scene View",

    // Script panel collapse (Bug 9)
    "script.hidePanel": "Hide script panel",
    "script.showPanel": "Show script panel",
    "script.showEditorHere": "Show Shot Editor here (instead of the script)",
    "script.showScript": "Show the script again",

    // PDF export progress (Bug 8)
    "pdf.exporting.title": "Generating PDF…",
    "pdf.exporting.text": "The storyboard is being generated. This can take a little while for large projects — please wait, this window will close automatically.",
    "pdf.exporting.error": "PDF export failed.",

    // Add shot dropdown (Bug 5)
    "scenes.addShotNoText": "Add shot without text",
    "scenes.addShot.more": "More options",

    // MASTER take (Bug 3) + shot editor
    "shotEditor.master": "MASTER",
    "shotEditor.master.title": "Mark this shot as the master take",
    "shotEditor.expandHint": "Double-click to expand the Shot Editor",

    // Shot editor image (Bug 6)
    "field.image": "Image",

    // Script panel empty states
    "script.empty.title": "No script loaded",
    "script.empty.text":
      "Import a .txt (or .fountain) file to display it here.",
    "script.empty.cta": "Import a script",

    // Script panel - scene preview
    "scenePreview.title": "Preview scene: {title}",
    "scenePreview.instruction": "Select the text here",
    "scenePreview.empty.title": "No scene selected",
    "scenePreview.empty.text":
      "Select a scene in panel 2, then come back here to select the text to assign to a shot.",
    "scenePreview.selectShotTip": "Click to select the shot",

    // Project file
    "project.invalid": "❌ Invalid project save file (JSON).",

    // Settings
    "settings.title": "⚙️ Settings",
    "settings.alt-title": "Preferences",
    "settings.language": "Language",
    "settings.language.help":
      "Exports (XLSX / HTML / CSV / PDF) will use the selected language.",
    "settings.lang.en": "English",
    "settings.lang.fr": "French",

    "settings.countLinkedShots": "Count linked shots in totals",
    "settings.countLinkedShots.help": "If disabled, linked plans are not counted in the various totals, only their original plans.",

    "settings.linkedLetterReorder": "Reorder linked-shot letters when moved",
    "settings.linkedLetterReorder.help": "When enabled, moving a linked shot updates its letter (A/B/C) to match the list order. The base number and other shots are not renumbered.",

    "settings.sceneMoveMode": "Move mode (organization)",
    "settings.sceneMoveMode.text": "Move the script text",
    "settings.sceneMoveMode.visual": "Reorder display only",
    "settings.sceneMoveMode.help": "In organization mode, choose whether moving a scene (or a shot into another scene) physically moves the corresponding text in the script (default) or only reorders the display without changing the text.",

    "settings.shotReorderMovesText": "Reorder script text when renumbering shots",
    "settings.shotReorderMovesText.help": "When enabled, reordering or renumbering shots within a scene also reorders their assigned text blocks in the script so the script order follows the shots.",

    "settings.shotPrefix": "Shot number prefix",
    "settings.shotPrefix.help": "Choose the prefix shown before shot numbers in the app.",
    "settings.shotPrefix.p": "P",
    "settings.shotPrefix.sh": "SH",
    "settings.shotPrefix.none": "No prefix",

    "settings.termsLang": "Shot Editor cinema terminology",
    "settings.termsLang.auto": "Auto (same as interface)",
    "settings.termsLang.fr": "French terms",
    "settings.termsLang.en": "English terms",
    "settings.termsLang.help":
      "Changes the wording in Shot Editor suggestion fields (shot size, angle, focus, etc.).",


    // About
    "about.title": "ℹ️ About",
    "about.created": "🪪 Created and developed by {name}, 2026",
    "about.tip": "❤️ Make a tip:",
    "about.mail": "📧 Contact: {name}",
    "about.mail-phrase": "Report a bug, propose a feature, or just spread some love?",
    "about.thanks": "🦾 Thanks: {name}",
    "about.notStoredOnline": "🔒 Your scripts, projects and images are never stored online: everything stays locally in your browser. Remember to save your project (.json) to keep your work.",
    "about.free": "🆓 This software is free, but please credit it in your end credits.",
    "about.stack": "Programmed in React.JS – Designed with Figma",
    "about.ai":
      "This application was programmed with the help of ChatGPT and Claude.",
    "about.version": "🔖 Version: ",

    // PDF config modal
    "pdf.modal.title": "🧾 Export PDF – Canva Storyboard",
    "pdf.projectName": "Project Name",
    "pdf.projectName.placeholder": "My project",
    "pdf.group.1": "Group 1",
    "pdf.group.2": "Group 2",
    "pdf.group.3": "Group 3",
    "pdf.field.sceneNumber": "Scene",
    "pdf.field.shotNumber": "Shot",
    "pdf.field.intExt": "In/Ext",
    "pdf.field.location": "Location",
    "pdf.field.timeOfDay": "Day",
    "pdf.field.shotSize": "Shot size",
    "pdf.field.angle": "Angle",
    "pdf.field.focus": "Focus",
    "pdf.field.framing": "Framing",
    "pdf.field.movement": "Movement",
    "pdf.field.focal": "Lens",
    "pdf.field.lights": "Setup lights",
    "pdf.field.estimation": "Time estimate",
    "pdf.field.ratio": "Ratio",
    "pdf.field.colorFormat": "Color format",
    "pdf.field.description": "Description",
    "pdf.field.scriptText": "Script",
    "pdf.field.image": "Image",
    "pdf.field.notes": "Notes space",

    "pdf.image.title": "Image",
    "pdf.image.none": "No images were imported into the shots.",
    "pdf.image.helpEnable": "Enable “Image” to choose its placement.",
    "pdf.image.underScript": "Under the script (default)",
    "pdf.image.inFrame": "In the main box",

    "pdf.notes.title": "Notes space",
    "pdf.notes.lines": "Lines",
    "pdf.notes.helpEnable":
      "Enable “Notes space” to choose the number of lines.",

    "pdf.download": "Download PDF",

    // CSV breakdown import (Bug 2)
    "csv.modal.title": "Import shot breakdown (CSV)",
    "csv.modal.help": "Match each Shotlist field to a column from your CSV file. Unmatched fields are left empty. Shots are created as inserts (no assigned script text).",
    "csv.rows": "{count} row(s) to import",
    "csv.hasHeader": "First row is a header",
    "csv.field.none": "— (ignore)",
    "csv.field.scene": "Scene",
    "csv.field.scene.help": "Map the Scene column above, then match each CSV scene value to one of your existing scenes below.",
    "csv.scene.mapTitle": "Scene correspondence (CSV value → scene)",
    "csv.scene.allTitle": "No scene column: import all shots into",
    "csv.scene.choose": "— Choose a scene —",
    "csv.scene.emptyValue": "(empty)",
    "csv.scene.include": "Import this scene's shots",
    "csv.import.cta": "Import shots",
    "csv.cancel": "Cancel",
    "csv.empty": "The CSV file appears to be empty.",
    "csv.needScenes": "Import a script first so shots can be attached to scenes.",
    "csv.imported": "✅ {count} shot(s) imported from CSV.",

    // PDF global settings (Bug 8)
    "pdf.global.title": "Global settings",
    "pdf.global.alwaysShowEmpty": "Always show sections even when empty",
    "pdf.global.alwaysShowEmpty.help": "If disabled, a section (Description, Script…) is hidden when its field is empty for that shot.",

    // PDF cover / credits pages (Bug 11)
    "pdf.pages.title": "Cover & credits pages",
    "pdf.cover.enable": "Add a cover page",
    "pdf.cover.placeholder": "Text shown on the cover page (under the project title)…",
    "pdf.credits.enable": "Add a credits page",
    "pdf.credits.placeholder": "Text shown on the final credits page…",
    "pdf.cover.pageTitle": "",
    "pdf.credits.pageTitle": "Credits",

    // Script view
    "script.refresh": "Update script",

    // Scene panel empty states
    "scenes.empty.noScript.title": "No script",
    "scenes.empty.noScript.text":
      "Import/paste a script into “Script”.",
    "scenes.empty.noScenes.title": "Not yet analyzed",
    "scenes.empty.noScenes.text": "Click “Detect scenes”.",
    "scenes.empty.selectScene": "Select a scene",

    // Scene panel actions
    "scenes.addShot": "Add Shot",
    "scenes.updateText": "Update text",
    "scenes.confirm": "Confirm",
    "scenes.cancel": "Cancel",
    "scenes.confirmInline":
      "⚠️ Replace the assigned text of this shot with the current selection?",

    // Display mode
    "mode.classic.title": "Classic list",
    "mode.classic.sub": "Tags, image and assigned text",
    "mode.compact.title": "Compact list",
    "mode.compact.sub":
      "Smaller, without image and assigned text",
    "mode.big.title": "Big picture",
    "mode.big.sub": "Large image, without assigned text",

    // Shots list
    "shots.none": "No shots created.",
    "shots.none.step1":
      "- Set panel 1 to Scene View using the clap button 🎬",
    "shots.none.step2":
      "- Select a passage in your scene that would be a shot",
    "shots.none.step3":
      "- Then click the “Add Shot” button above.",

    // Image actions
    "image.import": "Import",
    "image.remove": "Remove",

    // Reorder shots in list
    "shots.moveUp": "Move the selected shot up (renumbers)",
    "shots.moveDown": "Move the selected shot down (renumbers)",
    "shots.reorder": "Reorder selected shot",
    "shots.reorder.confirmTitle": "Renumber shots?",
    "shots.reorder.confirmText": "Moving this shot will change its number and renumber the other shots in the scene. Continue?",
    "shots.reorder.confirm": "Yes, renumber",

    // Organization mode
    "organize.toggle": "Organize",
    "organize.toggle.title": "Toggle organization mode (scenes & shots)",
    "organize.multi": "Select several at once",
    "organize.multi.short": "Multi",
    "organize.shots.toolbar": "Shot organization",
    "organize.shots.moveUp": "Move selected shot(s) to the previous scene",
    "organize.shots.moveDown": "Move selected shot(s) to the next scene",
    "organize.shots.delete": "Delete selected shot(s)",
    "organize.scenes.toolbar": "Scene organization",
    "organize.scenes.rename": "Rename scene",
    "organize.scenes.rename.apply": "Rename",
    "organize.scenes.moveUp": "Move selected scene(s) up",
    "organize.scenes.moveDown": "Move selected scene(s) down",
    "organize.scenes.delete": "Delete / restore selected scene(s)",
    "organize.conflict.title": "Change selection?",
    "organize.conflict.plans": "Heads up: you have selected shots to organize. Do you want to deselect them so you can select a scene to organize instead?",
    "organize.conflict.scenes": "Heads up: you have selected scenes to organize. Do you want to deselect them so you can select a shot to organize instead?",
    "organize.conflict.deselect": "Yes, deselect",
    "organize.hint": "Select a shot or a scene to organize",
    "organize.shots.moveToScene": "Move selected shot(s) into this scene",
    "organize.shots.moveUpList": "Move selected shot(s) up in the scene",
    "organize.shots.moveDownList": "Move selected shot(s) down in the scene",
    "organize.shots.renumber": "Renumber the scene's shots",
    "organize.shots.renumber.title": "Renumber shots",
    "organize.shots.renumber.help": "Reorder the scene's shots with the arrows; numbers are reassigned in this order when you apply.",
    "organize.shots.renumber.apply": "Apply",
    "organize.delete.confirmTitle": "Confirm deletion",
    "organize.delete.confirmShots": "Delete {count} selected shot(s)? This cannot be undone.",
    "organize.delete.confirmScenes": "Delete {count} selected scene(s)? They will be crossed out and their shots hidden from the shotlist (reversible).",
    "organize.delete.confirm": "Delete",

    "shots.aboveAnchor.title": "Move above the master (-A)?",
    "shots.aboveAnchor.text": "This linked shot would be placed above its master (-A). Do you want to make this shot the new master (-A) of the group?",
    "shots.aboveAnchor.yes": "Yes, make it the master",
    "shots.aboveAnchor.no": "No, cancel",

    // Shot fields editor
    "shotFields.button": "⚙️",
    "shotFields.modal.title": "Shot Fields Editor",
    "shotFields.modal.help": "Here you can modify the graphic display and the order of the tags applied in the Shot Editor",
    "shotFields.hide": "Hide this tag in the shot list",
    "shotFields.show": "Show this tag in the shot list",
    "common.reset": "Reset",
    "common.close": "Apply",

    // Shot editor
    "shotEditor.title": "🔧 Shot Editor",
    "shotEditor.empty":
      "Click on a shot in the list to edit it.",
    "shotEditor.desc.placeholder": "Description of the shot",
    "shotEditor.color.choose": "Choose a color",
    "shotEditor.color.default": "Default",
    "shotEditor.color.revert": "Revert to default color",

    "field.shotSize": "Shot size",
    "field.angle": "Angle",
    "field.focus": "Focus",
    "field.framing": "Framing",
    "field.movement": "Movement",
    "field.lens": "Lens (mm)",
    "field.lights": "Setup lights",
    "field.estimation": "Estimate (sec)",
    "field.ratio": "Ratio",
    "field.colorFormat": "Color format",
    "field.linkedShot": "Linked Shots",
    "linkedShot.none": "None",

    // Color format
    "colorFmt.color": "Color",
    "colorFmt.monochrome": "Monochrome",
    "colorFmt.bw": "Black & white",

    // Scene editor (⋮)
    "sceneEditor.open": "Scene settings",
    "sceneEditor.title": "Scene settings — {title}",
    "sceneEditor.help": "The screen ratio and color format apply to all shots of this scene.",
    "sceneEditor.inherit": "Use global setting",
    "shotEditor.inheritScene": "Inherit from scene",

    "shotEditor.assigned": "Text assigned to the shot",

    "shotEditor.delete": "Delete Shot",
    "shotEditor.delete.confirm": "Confirm deletion",
    "shotEditor.delete.title": "Delete shot",
    "shotEditor.delete.confirmTitle": "Click again to confirm",

    "shotEditor.reset": "Reset",
    "shotEditor.reset.confirm": "Confirm reset",
    "shotEditor.reset.title": "Reset shot data",
    "shotEditor.reset.confirmTitle": "Click again to confirm",

    "shotEditor.clearText": "Clear assigned text",
    "shotEditor.clearText.confirm": "Confirm clear",
    "shotEditor.clearText.title": "Remove the script text assigned to this shot",
    "shotEditor.clearText.confirmTitle": "Click again to confirm",

    "shotEditor.number": "Shot #",
    "shotEditor.number.title": "Change this shot's number; the others resync accordingly.",

    // Scene list totals
    "totals.title": "TOTAL",
    "totals.time": "Time",
    "totals.shots": "Shots",
    "totals.scenes": "Scenes",

    // Fallback labels
    "fallback.scene": "Scene {n}",

    // Export headers
    "export.col.sceneIndex": "Scene",
    "export.col.shotNumber": "Shot",
    "export.col.intExt": "In/Ext",
    "export.col.location": "Location",
    "export.col.timeOfDay": "Day",
    "export.col.shotSize": "Shot size",
    "export.col.angle": "Angle",
    "export.col.lensMm": "Lens (mm)",
    "export.col.focus": "Focus",
    "export.col.framing": "Framing",
    "export.col.movement": "Movement",
    "export.col.lights": "Setup lights",
    "export.col.durationSec": "Duration (sec)",
    "export.col.description": "Description",
    "export.col.ratio": "Ratio",
    "export.col.colorFormat": "Color format",
    "export.col.image": "Image",
    "export.col.scriptText": "Script text",
    "export.col.master": "Master",

    // Export other labels
    "export.html.title": "Shotlist",
    "export.readme.title": "PACKAGE EXPORT",
    "export.readme.contentTitle": "Content",
    "export.readme.content1":
      "- shots.csv: the shot list in CSV format (Excel/Sheets/Notion).",
    "export.readme.content2":
      "- shots.xlsx: the shot list in XLSX format.",
    "export.readme.content3":
      "- shots.html + images/: a Notion-friendly HTML version.",
    "export.readme.usageTitle": "Usage",
    "export.readme.usage1":
      "- Notion: import shots.html and the images folder.",
    "export.readme.usage2":
      "- Excel/Sheets: open shots.xlsx or import shots.csv.",

    // PDF export (inside PDF)
    "pdf.header.project": "Project:",
    "pdf.header.pages": "Pages:",
    "pdf.box.description": "Description",
    "pdf.box.script": "Script",
    "pdf.box.image": "Image",
    "pdf.tag.shotSize": "Shot size",
    "pdf.tag.angle": "Angle",
    "pdf.tag.focus": "Focus",
    "pdf.tag.framing": "Framing",
    "pdf.tag.movement": "Movement",
    "pdf.tag.focal": "Lens",
    "pdf.tag.lights": "Setup lights",
    "pdf.tag.estimation": "Estimate",
    "pdf.tag.ratio": "Ratio",
    "pdf.tag.colorFormat": "Color",

    // MODAL UPDATE SCRIPT
    "script.refresh.modalTitle": "Update script",
    "script.refresh.fromEditor": "From editor",
    "script.refresh.fromEditor.desc": "Use the current text in the script editor (manual changes).",
    "script.refresh.fromEditor.cta": "Update from editor",
    "script.refresh.fromFile": "Reimport file (.txt/.fountain)",
    "script.refresh.fromFile.desc": "Pick a file and update scenes/shots while keeping assigned texts.",
    "script.refresh.fromFile.cta": "Choose file",
    "script.refresh.cancel": "Cancel",
    "script.refresh.noChanges": "No changes to apply",

    // Frame format
    "settings.frameFormat": "Shot frame format",
    "settings.frameFormat.help": "Changes the visual frame format of shots in the app and exports.",

  },

  fr: {
    // Changelog
    "changelog.title": "Mises à jour",
    "changelog.added": "Ajouts",
    "changelog.improved": "Améliorations",
    "changelog.fixed": "Réparations",

    // Burger menu
    "menu.save": "Sauvegarder le projet",
    "menu.import": "Importer",
    "menu.import.script": "Importer .fountain / .txt",
    "menu.import.project": "Importer sauvegarde (.json)",
    "menu.import.csv": "Importer découpage (.csv)",
    "menu.import.csv.needScript": "Importez d'abord un scénario",
    "menu.export": "Exporter",
    "menu.export.excel": "Excel / Sheets (.xlsx)",
    "menu.export.notion": "Notion (HTML + images)",
    "menu.export.full": "Full Package (.zip)",
    "menu.export.pdf": "PDF Canva Storyboard (A4)",
    "menu.export.script": "Scénario",
    "menu.export.scriptTxt": "Scénario en .txt",
    "menu.export.scriptFountain": "Scénario en .fountain",
    "menu.lexic": "Lexique (bientôt)",
    "menu.wiki": "Wiki (bientôt)",
    "menu.settings": "Réglages",
    "menu.about": "À propos",

    // DICTS
    "script.refresh.deletedShots": "🗑️ {count} plan(s) supprimé(s) car leur texte assigné n’existe plus.",

    // Save project popup
    saveProjectPopup_title: "Vous avez sauvegardé votre projet !",
    saveProjectPopup_p1:
      "Vous venez de télécharger votre fichier de sauvegarde au format .json sur votre ordinateur.",
    saveProjectPopup_p2:
      "L'application n'héberge pas votre sauvegarde en ligne. Il vous faut donc la conserver localement sur votre ordinateur.",
    saveProjectPopup_p3:
      "Pour retrouver votre projet vous devez importer votre fichier de sauvegarder dans l'onglet Menu > Importer > Importer projet (.json)",
    saveProjectPopup_ok: "J'ai compris !",
    menu_saveProject: "Sauvegarder le projet",

    // Panels
    "panel.script.title": "SCÉNARIO",
    "panel.scenes.title": "SCÈNES & PLANS",
    "badge.scenes": "Scènes : {count}",
    "badge.shots": "Plans : {count}",

    // View toggle
    "view.script": "Vue Scénario",
    "view.scene": "Vue Scène",

    // Repli du panneau scénario (Bug 9)
    "script.hidePanel": "Masquer le scénario",
    "script.showPanel": "Afficher le scénario",
    "script.showEditorHere": "Afficher le Shot Editor ici (à la place du scénario)",
    "script.showScript": "Réafficher le scénario",

    // Progression export PDF (Bug 8)
    "pdf.exporting.title": "Génération du PDF…",
    "pdf.exporting.text": "Le storyboard est en cours de génération. Cela peut prendre un peu de temps pour les gros projets — merci de patienter, cette fenêtre se fermera automatiquement.",
    "pdf.exporting.error": "L’export PDF a échoué.",

    // Menu déroulant Ajouter Plan (Bug 5)
    "scenes.addShotNoText": "Ajouter Plan sans texte",
    "scenes.addShot.more": "Plus d’options",

    // Prise MASTER (Bug 3) + shot editor
    "shotEditor.master": "MASTER",
    "shotEditor.master.title": "Marquer ce plan comme prise master",
    "shotEditor.expandHint": "Double-cliquez pour déployer le Shot Editor",

    // Image dans le shot editor (Bug 6)
    "field.image": "Image",

    // Script panel empty states
    "script.empty.title": "Aucun script chargé",
    "script.empty.text":
      "Importe un fichier .txt (ou .fountain) pour l’afficher ici.",
    "script.empty.cta": "Importer un script",

    // Script panel - scene preview
    "scenePreview.title": "Aperçu scène : {title}",
    "scenePreview.instruction": "Sélectionne le texte ici",
    "scenePreview.empty.title": "Aucune scène sélectionnée",
    "scenePreview.empty.text":
      "Sélectionne une scène dans le panneau 2, puis reviens ici pour sélectionner le texte à assigner à un plan.",
    "scenePreview.selectShotTip": "Cliquer pour sélectionner le plan",

    // Project file
    "project.invalid": "❌ Fichier de sauvegarde invalide (JSON).",

    // Settings
    "settings.title": "⚙️ Réglages",
    "settings.alt-title": "Préférences",
    "settings.language": "Langue",
    "settings.language.help":
      "Les exports (XLSX / HTML / CSV / PDF) utilisent la langue sélectionnée.",
    "settings.lang.en": "Anglais",
    "settings.lang.fr": "Français",

    "settings.countLinkedShots": "Compter les plans liés dans les totaux",
    "settings.countLinkedShots.help": "Si désactivé, les plans liés ne sont pas comptabilisés dans les différents totaux mais uniquement leurs plans d'origines.",

    "settings.linkedLetterReorder": "Renuméroter les lettres des plans liés lors d'un déplacement",
    "settings.linkedLetterReorder.help": "Si activé, déplacer un plan lié met à jour sa lettre (A/B/C) selon l'ordre de la liste. Le numéro de base et les autres plans ne sont pas renumérotés.",

    "settings.sceneMoveMode": "Déplacement (organisation)",
    "settings.sceneMoveMode.text": "Déplacer le texte du scénario",
    "settings.sceneMoveMode.visual": "Réordonner l'affichage seulement",
    "settings.sceneMoveMode.help": "En mode organisation, choisissez si déplacer une scène (ou un plan vers une autre scène) déplace physiquement le texte correspondant dans le scénario (par défaut) ou réordonne seulement l'affichage sans toucher au texte.",

    "settings.shotReorderMovesText": "Réordonner le texte du scénario lors de la renumérotation des plans",
    "settings.shotReorderMovesText.help": "Si activé, réordonner ou renuméroter des plans dans une scène réordonne aussi leurs blocs de texte assignés dans le scénario, pour que l'ordre du texte suive celui des plans.",

    "settings.shotPrefix": "Préfixe des plans",
    "settings.shotPrefix.help": "Choisissez le préfixe affiché devant les numéros de plan dans l’application.",
    "settings.shotPrefix.p": "P",
    "settings.shotPrefix.sh": "SH",
    "settings.shotPrefix.none": "Aucun",

    "settings.termsLang": "Terminologie cinéma du Shot Editor",
    "settings.termsLang.auto": "Auto (comme l’interface)",
    "settings.termsLang.fr": "Termes français",
    "settings.termsLang.en": "Termes anglais",
    "settings.termsLang.help":
      "Change les termes affichés dans les champs à propositions du Shot Editor (taille de plan, angle, focus, etc.).",

    // About
    "about.title": "ℹ️ À propos",
    "about.created": "🪪 Créé et développé par {name}, 2026",
    "about.tip": "❤️ Faire un tip :",
    "about.mail": "📧 Contact : {name}",
    "about.mail-phrase": "Signaler un bug, proposer une fonctionnalité, ou juste partager ta satisfaction ?",
    "about.thanks": "🦾 Merci : {name}",
    "about.notStoredOnline": "🔒 Vos scénarios, projets et images ne sont jamais conservés en ligne : tout reste en local dans votre navigateur. Pensez à sauvegarder votre projet (.json) pour conserver votre travail.",
    "about.free": "🆓 Le logiciel est gratuit, mais merci de le citer au générique de fin.",
    "about.stack": "Programmé en React.JS – Designé avec Figma",
    "about.ai":
      "Cette application a été programmée avec l’aide de ChatGPT et Claude.",
    "about.version": "🔖 Version : ",

    // PDF config modal
    "pdf.modal.title": "🧾 Export PDF – Canva Storyboard",
    "pdf.projectName": "Nom du projet",
    "pdf.projectName.placeholder": "Mon projet",
    "pdf.group.1": "Groupe 1",
    "pdf.group.2": "Groupe 2",
    "pdf.group.3": "Groupe 3",
    "pdf.field.sceneNumber": "Scène",
    "pdf.field.shotNumber": "Plan",
    "pdf.field.intExt": "Int/Ext",
    "pdf.field.location": "Lieu",
    "pdf.field.timeOfDay": "Jour",
    "pdf.field.shotSize": "Échelle",
    "pdf.field.angle": "Angle",
    "pdf.field.focus": "Focus",
    "pdf.field.framing": "Cadrage",
    "pdf.field.movement": "Mouvement",
    "pdf.field.focal": "Focale",
    "pdf.field.estimation": "Estimation temps",
    "pdf.field.lights": "Lumière",
    "pdf.field.ratio": "Ratio",
    "pdf.field.colorFormat": "Format couleur",
    "pdf.field.description": "Description",
    "pdf.field.scriptText": "Script",
    "pdf.field.image": "Image",
    "pdf.field.notes": "Espace notes",

    "pdf.image.title": "Image",
    "pdf.image.none": "Aucune image n’a été importée dans les plans.",
    "pdf.image.helpEnable":
      "Active “Image” pour choisir son placement.",
    "pdf.image.underScript": "En dehors de la case à dessin (défaut)",
    "pdf.image.inFrame": "Dans la case à dessin",

    "pdf.notes.title": "Espace notes",
    "pdf.notes.lines": "Lignes",
    "pdf.notes.helpEnable":
      "Active “Espace notes” pour choisir le nombre de lignes.",

    "pdf.download": "Télécharger le PDF",

    // Import découpage CSV (Bug 2)
    "csv.modal.title": "Importer un découpage (CSV)",
    "csv.modal.help": "Associe chaque champ de Shotlist à une colonne de ton fichier CSV. Les champs non associés restent vides. Les plans sont créés en tant qu'inserts (sans texte de scénario assigné).",
    "csv.rows": "{count} ligne(s) à importer",
    "csv.hasHeader": "La première ligne est un en-tête",
    "csv.field.none": "— (ignorer)",
    "csv.field.scene": "Scène",
    "csv.field.scene.help": "Mappe la colonne Scène ci-dessus, puis associe chaque valeur de scène du CSV à l'une de tes scènes existantes ci-dessous.",
    "csv.scene.mapTitle": "Correspondance des scènes (valeur CSV → scène)",
    "csv.scene.allTitle": "Aucune colonne scène : importer tous les plans dans",
    "csv.scene.choose": "— Choisir une scène —",
    "csv.scene.emptyValue": "(vide)",
    "csv.scene.include": "Importer les plans de cette scène",
    "csv.import.cta": "Importer les plans",
    "csv.cancel": "Annuler",
    "csv.empty": "Le fichier CSV semble vide.",
    "csv.needScenes": "Importe d'abord un scénario pour rattacher les plans aux scènes.",
    "csv.imported": "✅ {count} plan(s) importé(s) depuis le CSV.",

    // Réglages globaux PDF (Bug 8)
    "pdf.global.title": "Réglages globaux",
    "pdf.global.alwaysShowEmpty": "Faire apparaître les sections même sans information",
    "pdf.global.alwaysShowEmpty.help": "Si désactivé, une section (Description, Script…) est masquée quand le champ est vide pour le plan concerné.",

    // Pages couverture / crédits PDF (Bug 11)
    "pdf.pages.title": "Pages couverture & crédits",
    "pdf.cover.enable": "Ajouter une page de couverture",
    "pdf.cover.placeholder": "Texte affiché sur la page de couverture (sous le titre du projet)…",
    "pdf.credits.enable": "Ajouter une page de crédits",
    "pdf.credits.placeholder": "Texte affiché sur la page de crédits finale…",
    "pdf.cover.pageTitle": "",
    "pdf.credits.pageTitle": "Crédits",

    // Script View
    "script.refresh": "Mettre à jour le scénario",

    // Scene panel empty states
    "scenes.empty.noScript.title": "Aucun script",
    "scenes.empty.noScript.text":
      "Importe/colle un script dans “Scénario”.",
    "scenes.empty.noScenes.title": "Pas encore analysé",
    "scenes.empty.noScenes.text": "Clique sur “Détecter scènes”.",
    "scenes.empty.selectScene": "Sélectionne une scène",

    // Scene panel actions
    "scenes.addShot": "Ajouter Plan",
    "scenes.updateText": "Mettre à jour texte",
    "scenes.confirm": "Confirmer",
    "scenes.cancel": "Annuler",
    "scenes.confirmInline":
      "⚠️ Remplacer le texte assigné de ce plan par la sélection actuelle ?",

    // Display mode
    "mode.classic.title": "Liste classique",
    "mode.classic.sub": "Tags, image et texte assigné",
    "mode.compact.title": "Liste compacte",
    "mode.compact.sub":
      "Plus petit, sans image ni texte assigné",
    "mode.big.title": "Big picture",
    "mode.big.sub": "Grande image, sans texte assigné",

    // Shots list
    "shots.none": "Aucun plan créé.",
    "shots.none.step1":
      "- Mets le panneau 1 en Vue scène avec le bouton clap 🎬",
    "shots.none.step2":
      "- Sélectionne un passage dans ta scène qui sera un plan",
    "shots.none.step3":
      "- Puis clique sur le bouton “Ajouter Plan” ci-dessus.",

    // Image actions
    "image.import": "Importer",
    "image.remove": "Supprimer",

    // Réordonner les plans dans la liste
    "shots.moveUp": "Monter le plan sélectionné (renumérote)",
    "shots.moveDown": "Descendre le plan sélectionné (renumérote)",
    "shots.reorder": "Réordonner le plan sélectionné",
    "shots.reorder.confirmTitle": "Renuméroter les plans ?",
    "shots.reorder.confirmText": "Déplacer ce plan va changer son numéro et renuméroter les autres plans de la scène. Continuer ?",
    "shots.reorder.confirm": "Oui, renuméroter",

    // Mode organisation
    "organize.toggle": "Organiser",
    "organize.toggle.title": "Activer le mode organisation (scènes & plans)",
    "organize.multi": "Sélectionner plusieurs à la fois",
    "organize.multi.short": "Multi",
    "organize.shots.toolbar": "Organisation des plans",
    "organize.shots.moveUp": "Déplacer le(s) plan(s) vers la scène précédente",
    "organize.shots.moveDown": "Déplacer le(s) plan(s) vers la scène suivante",
    "organize.shots.delete": "Supprimer le(s) plan(s) sélectionné(s)",
    "organize.scenes.toolbar": "Organisation des scènes",
    "organize.scenes.rename": "Renommer la scène",
    "organize.scenes.rename.apply": "Renommer",
    "organize.scenes.moveUp": "Monter la/les scène(s) sélectionnée(s)",
    "organize.scenes.moveDown": "Descendre la/les scène(s) sélectionnée(s)",
    "organize.scenes.delete": "Supprimer / restaurer la/les scène(s) sélectionnée(s)",
    "organize.conflict.title": "Changer de sélection ?",
    "organize.conflict.plans": "Attention vous avez sélectionné des plans à organiser, voulez-vous les désélectionner pour pouvoir sélectionner une scène à organiser ?",
    "organize.conflict.scenes": "Attention vous avez sélectionné des scènes à organiser, voulez-vous les désélectionner pour pouvoir sélectionner un plan à organiser ?",
    "organize.conflict.deselect": "Oui, désélectionner",
    "organize.hint": "Sélectionnez un plan ou une scène à organiser",
    "organize.shots.moveToScene": "Déplacer le(s) plan(s) dans cette scène",
    "organize.shots.moveUpList": "Monter le(s) plan(s) dans la scène",
    "organize.shots.moveDownList": "Descendre le(s) plan(s) dans la scène",
    "organize.shots.renumber": "Renuméroter les plans de la scène",
    "organize.shots.renumber.title": "Renuméroter les plans",
    "organize.shots.renumber.help": "Réordonnez les plans de la scène avec les flèches ; les numéros sont réattribués dans cet ordre à la validation.",
    "organize.shots.renumber.apply": "Appliquer",
    "organize.delete.confirmTitle": "Confirmer la suppression",
    "organize.delete.confirmShots": "Supprimer {count} plan(s) sélectionné(s) ? Cette action est irréversible.",
    "organize.delete.confirmScenes": "Supprimer {count} scène(s) sélectionnée(s) ? Elles seront barrées et leurs plans masqués de la shotlist (réversible).",
    "organize.delete.confirm": "Supprimer",

    "shots.aboveAnchor.title": "Passer au-dessus du master (-A) ?",
    "shots.aboveAnchor.text": "Ce plan lié va se retrouver au-dessus de son master (-A). Voulez-vous faire de ce plan le nouveau master (-A) du groupe ?",
    "shots.aboveAnchor.yes": "Oui, en faire le master",
    "shots.aboveAnchor.no": "Non, annuler",

    // Shot fields editor
    "shotFields.button": "⚙️",
    "shotFields.modal.title": "Editeur des tags de plan",
    "shotFields.modal.help": "Ici vous pouvez modifier l'affichage graphique et l'ordre des tags appliqués dans le Shot Editor",
    "shotFields.hide": "Masquer ce tag dans la liste des plans",
    "shotFields.show": "Afficher ce tag dans la liste des plans",
    "common.reset": "Réinitialiser",
    "common.close": "Appliquer",

    // Shot editor
    "shotEditor.title": "🔧 Shot Editor",
    "shotEditor.empty":
      "Clique sur un plan dans la liste pour l’éditer.",
    "shotEditor.desc.placeholder": "Description du plan",
    "shotEditor.color.choose": "Choisir une couleur",
    "shotEditor.color.default": "Défaut",
    "shotEditor.color.revert":
      "Revenir à la couleur par défaut",

    "field.shotSize": "Échelle",
    "field.angle": "Angle",
    "field.focus": "Focus",
    "field.framing": "Cadrage",
    "field.movement": "Mouvement",
    "field.lens": "Focale (mm)",
    "field.lights": "Lumière",
    "field.estimation": "Estimation (sec)",
    "field.ratio": "Ratio",
    "field.colorFormat": "Format couleur",
    "field.linkedShot": "Plans liés",
    "linkedShot.none": "Aucun",

    // Format couleur
    "colorFmt.color": "Couleurs",
    "colorFmt.monochrome": "Monochrome",
    "colorFmt.bw": "Noir et blanc",

    // Éditeur de scène (⋮)
    "sceneEditor.open": "Réglages de la scène",
    "sceneEditor.title": "Réglages de la scène — {title}",
    "sceneEditor.help": "Le ratio d'écran et le format couleur s'appliquent à tous les plans de cette scène.",
    "sceneEditor.inherit": "Utiliser le réglage global",
    "shotEditor.inheritScene": "Hériter de la scène",

    "shotEditor.assigned": "Texte assigné au plan",

    "shotEditor.delete": "Supprimer Plan",
    "shotEditor.delete.confirm": "Confirmer suppression",
    "shotEditor.delete.title": "Supprimer le plan",
    "shotEditor.delete.confirmTitle":
      "Cliquer à nouveau pour confirmer",
    
    "shotEditor.reset": "Réinitialiser",
    "shotEditor.reset.confirm": "Confirmer réinitialisation",
    "shotEditor.reset.title": "Réinitialiser les données du plan",
    "shotEditor.reset.confirmTitle": "Cliquer à nouveau pour confirmer",

    "shotEditor.clearText": "Supprimer texte assigné",
    "shotEditor.clearText.confirm": "Confirmer suppression",
    "shotEditor.clearText.title": "Retirer le texte du scénario assigné à ce plan",
    "shotEditor.clearText.confirmTitle": "Cliquer à nouveau pour confirmer",

    "shotEditor.number": "N° plan",
    "shotEditor.number.title": "Change le numéro de ce plan ; les autres se resynchronisent.",

    // Scene list totals
    "totals.title": "TOTAL",
    "totals.time": "Temps",
    "totals.shots": "Plans",
    "totals.scenes": "Scènes",

    // Fallback labels
    "fallback.scene": "Scène {n}",

    // Export headers
    "export.col.sceneIndex": "Scène",
    "export.col.shotNumber": "Plan",
    "export.col.intExt": "Int/Ext",
    "export.col.location": "Lieu",
    "export.col.timeOfDay": "Jour",
    "export.col.shotSize": "Échelle",
    "export.col.angle": "Angle",
    "export.col.lensMm": "Focale (mm)",
    "export.col.focus": "Focus",
    "export.col.framing": "Cadrage",
    "export.col.movement": "Mouvement",
    "export.col.durationSec": "Durée (sec)",
    "export.col.lights": "Lumière",
    "export.col.description": "Description",
    "export.col.ratio": "Ratio",
    "export.col.colorFormat": "Format couleur",
    "export.col.image": "Image",
    "export.col.scriptText": "Texte scénario",
    "export.col.master": "Master",

    // Export other labels
    "export.html.title": "Shotlist",
    "export.readme.title": "EXPORT PACKAGE",
    "export.readme.contentTitle": "Contenu",
    "export.readme.content1":
      "- shots.csv : la shotlist en CSV (Excel/Sheets/Notion).",
    "export.readme.content2":
      "- shots.xlsx : la shotlist en XLSX.",
    "export.readme.content3":
      "- shots.html + images/ : une version HTML compatible Notion.",
    "export.readme.usageTitle": "Utilisation",
    "export.readme.usage1":
      "- Notion : importe shots.html et le dossier images.",
    "export.readme.usage2":
      "- Excel/Sheets : ouvre shots.xlsx ou importe shots.csv.",

    // PDF export (inside PDF)
    "pdf.header.project": "Projet :",
    "pdf.header.pages": "Pages :",
    "pdf.box.description": "Description",
    "pdf.box.script": "Script",
    "pdf.box.image": "Image",
    "pdf.tag.shotSize": "Échelle",
    "pdf.tag.angle": "Angle",
    "pdf.tag.focus": "Focus",
    "pdf.tag.framing": "Cadrage",
    "pdf.tag.movement": "Mouvement",
    "pdf.tag.focal": "Focale",
    "pdf.tag.lights": "Lumière",
    "pdf.tag.estimation": "Estimation",
    "pdf.tag.ratio": "Ratio",
    "pdf.tag.colorFormat": "Couleur",

    // MODAL UPDATE SCRIPT
    "script.refresh.modalTitle": "Mettre à jour le scénario",
    "script.refresh.fromEditor": "Depuis l’éditeur",
    "script.refresh.fromEditor.desc": "Utilise le texte actuel dans la vue scénario (modifs manuelles).",
    "script.refresh.fromEditor.cta": "Mettre à jour",
    "script.refresh.fromFile": "Réimporter un fichier (.txt/.fountain)",
    "script.refresh.fromFile.desc": "Choisis un fichier et mets à jour scènes/plans en conservant les textes assignés.",
    "script.refresh.fromFile.cta": "Choisir un fichier",
    "script.refresh.cancel": "Annuler",
    "script.refresh.noChanges": "Aucune modification à appliquer",

    // Frame format
    "settings.frameFormat": "Format des cadres de plan",
    "settings.frameFormat.help": "Change le format visuel des cadres de plans dans l’application et les exports.",

  },
};

export function normalizeLang(lang) {
  const v = String(lang || "").toLowerCase();
  if (SUPPORTED_LANGS.includes(v)) return v;
  return "en";
}

export function getStoredLang() {
  try {
    return normalizeLang(window?.localStorage?.getItem(STORAGE_KEY) || "en");
  } catch {
    return "en";
  }
}

export function setStoredLang(lang) {
  const v = normalizeLang(lang);
  try {
    window?.localStorage?.setItem(STORAGE_KEY, v);
  } catch {
    // ignore
  }
  return v;
}

function interpolate(str, vars) {
  if (!vars) return str;
  return String(str).replace(/\{(\w+)\}/g, (_, k) => {
    const val = vars[k];
    return val === undefined || val === null ? "" : String(val);
  });
}

export function createTranslator(lang) {
  const l = normalizeLang(lang);
  const dict = DICTS[l] || DICTS.en;
  const fallback = DICTS.en;

  return function t(key, vars) {
    const k = String(key || "");
    const raw = dict[k] ?? fallback[k] ?? k;
    return interpolate(raw, vars);
  };
}

export function translate(lang, key, vars) {
  return createTranslator(lang)(key, vars);
}

const I18nContext = createContext({
  lang: "en",
  setLang: () => {},
  t: (k) => k,
});

export function I18nProvider({ children, initialLang }) {
  const [lang, setLangState] = useState(() =>
    normalizeLang(initialLang || getStoredLang() || "en")
  );

  const setLang = useCallback((next) => {
    const v = setStoredLang(next);
    setLangState(v);
  }, []);

  const t = useMemo(() => createTranslator(lang), [lang]);

  const value = useMemo(() => ({ lang, setLang, t }), [lang, setLang, t]);

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useI18n() {
  return useContext(I18nContext);
}
